import type { Money, Tour, TourDeparture, TourFareOffer } from "@getaways/core";
import type { ResosysTourBooking } from "@getaways/tours";
import { addDays, api, Badge, Banner, DateRangePicker, Empty, Field, go, optionsOf, PageHead, Select, todayIso, useAsync, useStoredState, type Notice } from "@getaways/extranet-ui";
import { useState } from "react";
import { amount, localDate } from "../lib/format";
import { AVAILABILITY, BRANDS } from "../lib/labels";
import { blankTraveller, toTraveller, TravellerFields, type Draft } from "../lib/traveller-form";

type Listed = Tour & { bookable: boolean };
type Fare = TourFareOffer & { sell: Money; sellPerPerson: Money };
interface Market { code: string; name: string; currency: string }

/**
 * Sell a tour end to end: the test booking page on staging, and the agents' tool. With GADVENTURES_MODE=demo or
 * live_read, holds and bookings go to the simulator: nothing is booked with G Adventures.
 */
export function SellPage() {
  const markets = useAsync(() => api<{ markets: Market[] }>("GET", "/v1/markets"), []);
  const [market, setMarket] = useStoredState<string>("tours.sell.market", "EU");
  const [brand, setBrand] = useStoredState<string>("tours.sell.brand", "azores-getaways");
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState(addDays(todayIso(), 30));
  const [to, setTo] = useState(addDays(todayIso(), 365));
  const [adults, setAdults] = useState(2);
  const [childAges, setChildAges] = useState("");
  const [tour, setTour] = useState<Listed>();
  const [departure, setDeparture] = useState<TourDeparture>();
  const [fare, setFare] = useState<Fare>();
  const [booking, setBooking] = useState<ResosysTourBooking>();

  const list = useAsync(() => api<{ tours: Listed[]; total: number }>("GET", `/v1/tours?from=${from}&to=${to}${q.length >= 2 ? `&q=${encodeURIComponent(q)}` : ""}&limit=60`), [from, to, q]);
  const ages = [...Array(adults).fill(30), ...childAges.split(",").map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n >= 0 && childAges.trim() !== "")];
  const departures = useAsync(() => (tour ? api<{ departures: TourDeparture[] }>("GET", `/v1/tours/${tour.code}/departures?from=${from}&to=${to}`) : Promise.resolve(undefined)), [tour?.code, from, to]);
  const fares = useAsync(() => (tour && departure ? api<{ fares: Fare[] }>("POST", "/v1/fares", { market, tourCode: tour.code, departureCode: departure.code, ages, channel: "agent" }) : Promise.resolve(undefined)), [departure?.code, ages.join(","), market]);
  const currency = markets.data?.markets.find((m) => m.code === market)?.currency;

  const restart = () => { setBooking(undefined); setFare(undefined); setDeparture(undefined); };
  if (booking) return <Booked booking={booking} onRestart={restart} />;
  if (tour && departure && fare) return <Travellers market={market} brand={brand} tour={tour} departure={departure} fare={fare} ages={ages} onBack={() => setFare(undefined)} onHeld={setBooking} />;

  return (
    <>
      <PageHead title="Sell a tour" sub="Search our catalogue, read departures and prices live, hold with the travellers' data, and confirm." />
      <div className="steps"><span className={!tour ? "on" : ""}>1 · Tour</span><span className={tour && !departure ? "on" : ""}>2 · Departure</span><span className={departure ? "on" : ""}>3 · Room</span><span>4 · Travellers and hold</span><span>5 · Confirm</span></div>
      <div className="card">
        <div className="grid2">
          <Field label="Market"><Select value={market} onChange={(m) => { setMarket(m); setFare(undefined); }} options={(markets.data?.markets ?? []).map((m) => ({ value: m.code, label: `${m.name} (${m.currency})` }))} /></Field>
          <Field label="Brand"><Select value={brand} onChange={setBrand} options={optionsOf(BRANDS)} /></Field>
          <div className="stack" style={{ gap: 4 }}>
            <span className="small muted">Starting between</span>
            <DateRangePicker value={{ from, to }} onChange={(r) => { setFrom(r.from); setTo(r.to); }} labels={["Starting from", "Starting to"]} />
          </div>
          <Field label="Adults"><input type="number" min={1} max={12} value={adults} onChange={(e) => setAdults(Math.max(1, Math.min(12, Number(e.target.value) || 1)))} /></Field>
          <Field label="Children's ages" help="On the start date, comma-separated"><input value={childAges} onChange={(e) => setChildAges(e.target.value)} placeholder="none" /></Field>
        </div>
      </div>

      {!tour && (
        <div className="card" style={{ padding: 0 }}>
          <form className="row" style={{ padding: "12px 14px", borderBottom: "1px solid var(--line)" }} onSubmit={(e) => { e.preventDefault(); setQ(text.trim()); }}>
            <input value={text} onChange={(e) => setText(e.target.value)} onBlur={() => setQ(text.trim())} placeholder="Tour, country or city" aria-label="Search tours" style={{ maxWidth: 320 }} />
            <span className="spacer" />
            {list.data && <span className="small muted">{list.data.total} tours</span>}
          </form>
          {list.error && <Banner notice={{ tone: "bad", text: list.error }} />}
          {list.data && list.data.tours.length === 0 ? <Empty>No tours. The catalogue may still be importing.</Empty> : (
            <table className="table">
              <thead><tr><th>Tour</th><th>Where</th><th>Days</th><th className="num">From (per person)</th><th /></tr></thead>
              <tbody>
                {(list.data?.tours ?? []).map((t) => (
                  <tr key={t.code} className="click" onClick={() => t.bookable && setTour(t)}>
                    <td>{t.name}<div className="small muted">{t.categories.map((c) => c.name).join(" · ")}</div></td>
                    <td className="small">{t.start?.name ?? "—"}{t.finish && t.finish.name !== t.start?.name ? ` → ${t.finish.name}` : ""}<div className="muted">{t.region}</div></td>
                    <td>{t.durationDays ?? "—"}</td>
                    <td className="num">{amount(t.leadPrices.find((p) => p.currency === currency) ?? t.leadPrices[0])}</td>
                    <td>{t.bookable ? <Badge tone="ok">bookable</Badge> : <Badge title="Open the supplier in Suppliers and markets first">not open</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tour && (
        <div className="card">
          <div className="row"><h2 style={{ margin: 0 }}>{tour.name}</h2><span className="spacer" /><button onClick={() => { setTour(undefined); setDeparture(undefined); }}>Change tour</button></div>
          <div className="muted small">{tour.durationDays ? `${tour.durationDays} days · ` : ""}{tour.start?.name}{tour.finish && tour.finish.name !== tour.start?.name ? ` → ${tour.finish.name}` : ""} · {tour.countries.join(", ")}</div>
          {tour.description && <p className="small">{tour.description}</p>}
          {tour.itinerary.length > 0 && (
            <div className="days" style={{ marginTop: 8 }}>
              {tour.itinerary.map((d) => <div className="day" key={d.day}><span className="n">Day {d.day}{d.toDay ? `–${d.toDay}` : ""}</span><span><strong>{d.title}</strong>{d.summary ? ` · ${d.summary}` : ""}{d.meals.length ? <span className="muted"> · {d.meals.join(", ")}</span> : ""}</span></div>)}
            </div>
          )}
        </div>
      )}

      {tour && !departure && (
        <div className="card" style={{ padding: 0 }}>
          <h2 style={{ padding: "12px 14px 0" }}>Departures</h2>
          {departures.loading && <div className="muted" style={{ padding: 14 }}>Reading departures live…</div>}
          {departures.error && <Banner notice={{ tone: "bad", text: departures.error }} />}
          {departures.data && departures.data.departures.length === 0 ? <Empty>No departures in these dates.</Empty> : (
            <table className="table">
              <thead><tr><th>Starts</th><th>Ends</th><th>Spaces</th><th className="num">From (per person)</th><th /></tr></thead>
              <tbody>
                {(departures.data?.departures ?? []).map((d) => {
                  const bookable = d.availability !== "sold_out" && d.availability !== "closed";
                  return (
                    <tr key={d.code} className={bookable ? "click" : ""} onClick={() => bookable && setDeparture(d)}>
                      <td>{localDate(d.start)}<div className="small muted mono">{d.sku}</div></td>
                      <td>{localDate(d.finish)}</td>
                      <td><Badge tone={AVAILABILITY[d.availability]![0]}>{AVAILABILITY[d.availability]![1]}</Badge>{d.spaces !== undefined && d.availability === "available" && <span className="small muted"> {d.spaces} left</span>}{d.guaranteed && <> <Badge tone="ok">guaranteed</Badge></>}</td>
                      <td className="num">{amount(d.leadPrices.find((p) => p.currency === currency) ?? d.leadPrices[0])}</td>
                      <td />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {departure && (
        <div className="card">
          <div className="row"><h2 style={{ margin: 0 }}>Rooms for {ages.length} traveller{ages.length === 1 ? "" : "s"} · {localDate(departure.start)}</h2><span className="spacer" /><button onClick={() => setDeparture(undefined)}>Change departure</button></div>
          {fares.loading && <div className="muted">Pricing live…</div>}
          {fares.error && <Banner notice={{ tone: "bad", text: fares.error }} />}
          {fares.data && fares.data.fares.length === 0 && <Empty>No room takes this party in {currency}. Try other ages or another departure.</Empty>}
          <div className="fares">
            {(fares.data?.fares ?? []).map((f) => (
              <button key={f.offerKey} className="fare" onClick={() => setFare(f)}>
                <div><strong>{f.roomName}</strong> <Badge tone={AVAILABILITY[f.availability]![0]}>{AVAILABILITY[f.availability]![1]}</Badge></div>
                <div className="price">{amount(f.sell)}</div>
                <div className="small muted">{f.travellers.map((t) => `${t.band.toLowerCase()} ${amount(t.price)}`).join(" · ")}</div>
                {f.deposit && <div className="small">Deposit {amount(f.deposit)}{f.fullPaymentToConfirm ? " · full payment to confirm" : ""}</div>}
                {f.localPayments.length > 0 && <div className="small muted">Paid on the trip: {f.localPayments.map((p) => `${p.label} ${amount(p.amount ?? p.min)}`).join(", ")}</div>}
                <div className="small muted">Our margin about {amount({ amountMinor: f.gross.amountMinor - f.net.amountMinor, currency: f.gross.currency })}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function Travellers({ market, brand, tour, departure, fare, ages, onBack, onHeld }: { market: string; brand: string; tour: Listed; departure: TourDeparture; fare: Fare; ages: number[]; onBack: () => void; onHeld: (b: ResosysTourBooking) => void }) {
  const [drafts, setDrafts] = useState<Draft[]>(() => ages.map(blankTraveller));
  const [problems, setProblems] = useState<Record<number, Record<string, string>>>({});
  const [notice, setNotice] = useState<Notice>();
  const [busy, setBusy] = useState(false);
  const set = (i: number, patch: Partial<Draft>) => setDrafts((list) => list.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  const hold = async () => {
    setBusy(true);
    setProblems({});
    setNotice(undefined);
    try {
      const ref = `GW-TEST${Date.now().toString(36).toUpperCase().slice(-5)}-1`;
      const b = await api<ResosysTourBooking>("POST", "/v1/holds", { market, tourCode: tour.code, departure, fare: { offerKey: fare.offerKey, roomCode: fare.roomCode, roomName: fare.roomName, localPayments: fare.localPayments }, ages, travellers: drafts.map(toTraveller), channel: "agent", brand, clientReference: ref });
      onHeld(b);
    } catch (err) {
      const issues = (err as { data?: { problems?: { traveller: number; field: string; message: string }[] } }).data?.problems;
      if (issues?.length) setProblems(issues.reduce<Record<number, Record<string, string>>>((acc, p) => ({ ...acc, [p.traveller]: { ...acc[p.traveller], [p.field]: p.message } }), {}));
      setNotice({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead title="Travellers" sub={`${tour.name} · ${localDate(departure.start)} · ${fare.roomName} · ${amount(fare.sell)}`} />
      {notice && <Banner notice={notice} onClose={() => setNotice(undefined)} />}
      <div className="callout" style={{ marginBottom: 12 }}>The operator creates a record for each traveller to hold the space: names as in the passport, and the age on the start date must be the one priced.</div>
      {drafts.map((d, i) => (
        <div className="card" key={i}>
          <h2>Traveller {i + 1}{i === 0 ? " (lead)" : ""} <span className="small muted">priced as {ages[i]} on {localDate(departure.start)}</span></h2>
          <TravellerFields value={d} onChange={(patch) => set(i, patch)} problems={problems[i]} />
        </div>
      ))}
      <div className="actions">
        <button className="primary" disabled={busy} onClick={() => void hold()}>{busy ? "Holding…" : `Hold · ${amount(fare.sell)}`}</button>
        <button onClick={onBack}>Back to rooms</button>
      </div>
    </>
  );
}

function Booked({ booking: initial, onRestart }: { booking: ResosysTourBooking; onRestart: () => void }) {
  const [booking, setBooking] = useState(initial);
  const [notice, setNotice] = useState<Notice>();
  const [busy, setBusy] = useState(false);
  const blocking = booking.requirements.filter((r) => r.stage === "confirmation");

  const confirm = async () => {
    setBusy(true);
    setNotice(undefined);
    try {
      setBooking(await api<ResosysTourBooking>("POST", `/v1/bookings/${booking.id}/book`));
    } catch (err) {
      setNotice({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead title={booking.status === "confirmed" ? "Confirmed" : "Held"} sub={`${booking.tour.name} · ${localDate(booking.departure.start)} · ${booking.room.name}`} />
      {notice && <Banner notice={notice} onClose={() => setNotice(undefined)} />}
      <div className="card">
        <dl className="kv">
          <dt>Our reference</dt><dd className="mono">{booking.clientReference}</dd>
          <dt>Supplier booking</dt><dd className="mono">{booking.supplier} {booking.supplierReference}</dd>
          <dt>Total</dt><dd>{amount(booking.sell)}</dd>
          {booking.holdExpiresAt && booking.status !== "confirmed" && <><dt>Held until</dt><dd>{new Date(booking.holdExpiresAt).toLocaleString("en-GB")}</dd></>}
          {booking.customerSchedule.length > 0 && <><dt>Customer pays</dt><dd>{booking.customerSchedule.map((d) => `${amount(d.amount)} ${d.kind} by ${localDate(d.dueDate)}`).join(" · ")}</dd></>}
          {booking.requirements.length > 0 && <><dt>Still needed</dt><dd>{booking.requirements.map((r) => `${r.traveller !== undefined ? `traveller ${r.traveller + 1}: ` : ""}${r.code.toLowerCase().replace(/_/g, " ")}${r.stage === "confirmation" ? " (to confirm)" : " (before travel)"}`).join(", ")}</dd></>}
        </dl>
        {booking.status === "held" && blocking.length > 0 && <div className="callout" style={{ marginTop: 10 }}>Complete the travellers' data in the booking before confirming.</div>}
        <div className="actions" style={{ marginTop: 12 }}>
          {booking.status === "held" && <button className="primary" disabled={busy || blocking.length > 0} onClick={() => void confirm()}>{busy ? "Confirming…" : `Confirm · ${amount(booking.sell)}`}</button>}
          <button onClick={() => go("bookings", booking.id)}>Open the booking</button>
          {booking.status === "held" && <button onClick={() => void api("POST", `/v1/bookings/${booking.id}/release`).then(onRestart)}>Release the hold</button>}
          <button onClick={onRestart}>Sell another</button>
        </div>
      </div>
    </>
  );
}
